import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  applyAssignment,
  fetchAssignmentLog,
} from "../../../business/redux/reducers/mcOpsDashboard/actions";
import { Icon } from "../icons";
import { fmtDate, fmtDateTime } from "../lib/format";
import { openJobPage } from "../lib/jobLink";
import { BoardTitleRow } from "../components/JobSearch";

/** Admin page: every decision the auto-assignment engine took, and why. */

const RULE_LABELS = { new: "New job", rule4: "Reassign (rule 4)" };

const Row = ({ row, onApply }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | applying | failed

  const apply = async () => {
    setStatus("applying");
    try {
      await onApply(row.id);
      setStatus("idle");
    } catch (e) {
      setStatus("failed");
    }
  };

  return (
    <>
      <tr>
        <td className="meta">{row.createdAt ? fmtDateTime(row.createdAt) : ""}</td>
        <td>
          <button
            type="button"
            className="mc-link"
            onClick={() => openJobPage(row.job.id, "s1")}
            title="Open job in new tab"
          >
            {row.job.jobNumber}
          </button>
          <div className="meta">
            <span>{row.job.clientName}</span> · {row.job.moveType} · {row.job.branch} · Move{" "}
            {row.job.moveDate ? fmtDate(row.job.moveDate) : "—"}
          </div>
        </td>
        <td>{RULE_LABELS[row.rule] || row.rule}</td>
        <td>
          {row.from ? (
            <div className="meta">
              from <span>{row.from.name}</span>
            </div>
          ) : null}
          {row.to ? <strong>{row.to.name}</strong> : <span className="mc-error">Nobody</span>}
        </td>
        <td>
          <div>{row.reason}</div>
          {row.candidates.length ? (
            <button type="button" className="mc-link meta" onClick={() => setOpen((v) => !v)}>
              {open ? "Hide" : "Show"} {row.candidates.length} candidate
              {row.candidates.length === 1 ? "" : "s"}
            </button>
          ) : null}
        </td>
        <td>
          {row.appliedAt ? (
            <span className="badge resolved">
              <span className="bdot" />
              Applied
            </span>
          ) : row.canApply ? (
            <button
              type="button"
              className="btn small primary"
              disabled={status === "applying"}
              onClick={apply}
            >
              <Icon name="check" />
              {status === "applying" ? "Applying…" : "Apply"}
            </button>
          ) : (
            <span className="badge gray">
              <span className="bdot" />
              {row.mode === "suggest" ? "Suggestion" : "Not applied"}
            </span>
          )}
          {status === "failed" ? <div className="meta mc-error">Could not apply</div> : null}
        </td>
      </tr>
      {open ? (
        <tr className="mc-candidates">
          <td colSpan={6}>
            {row.candidates.map((c) => (
              <div className="meta" key={c.user_id}>
                {c.name} — score {c.score} ({c.new3h} new in 3h, {c.overdue72h} overdue,{" "}
                {c.callsDue} calls due, {c.openJobs} open jobs)
              </div>
            ))}
          </td>
        </tr>
      ) : null}
    </>
  );
};

export const AssignmentLogPage = () => {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.mcOpsDashboard?.meta?.assignmentLog);
  const [days, setDays] = useState(7);

  useEffect(() => {
    dispatch(fetchAssignmentLog(days));
  }, [dispatch, days]);

  const onApply = (id) => dispatch(applyAssignment(id));

  return (
    <div className="page active">
      <BoardTitleRow title="Assignment log" />

      <div className="mc-log-tools">
        <label>
          Show{" "}
          <select aria-label="Show" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>today</option>
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
          </select>
        </label>
        {data ? (
          <span className="meta">
            {data.mode === "suggest"
              ? "Suggest mode: nothing is written to Zoho unless you press Apply."
              : "Assign mode: the engine writes to Zoho itself."}
          </span>
        ) : null}
      </div>

      {data?.error === 403 ? (
        <div className="panel-empty">Admin only. Sign in as a manager to see the assignment log.</div>
      ) : !data ? (
        <div className="panel-empty">Loading…</div>
      ) : !data.assignments.length ? (
        <div className="panel-empty">No decisions in this period.</div>
      ) : (
        <div className="mc-table-wrap">
          <table className="mc-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Job</th>
                <th>Why in the pool</th>
                <th>Coordinator</th>
                <th>Reason</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.assignments.map((row) => (
                <Row row={row} onApply={onApply} key={row.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
